import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import trLocale from '../locales/tr.json';
import enLocale from '../locales/en.json';

const locales = { tr: trLocale, en: enLocale };

const LanguageContext = createContext();

/**
 * Detect browser language, defaulting to 'tr' if not English-based.
 */
function detectBrowserLanguage() {
    try {
        const stored = localStorage.getItem('app_language');
        if (stored && locales[stored]) return stored;

        const browserLang = navigator.language || navigator.userLanguage || 'tr';
        return browserLang.startsWith('en') ? 'en' : 'tr';
    } catch {
        return 'tr';
    }
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(detectBrowserLanguage);

    useEffect(() => {
        try {
            localStorage.setItem('app_language', language);
            document.documentElement.setAttribute('lang', language);
        } catch {
            // localStorage might not be available
        }
    }, [language]);

    const toggleLanguage = useCallback(() => {
        setLanguage(prev => (prev === 'tr' ? 'en' : 'tr'));
    }, []);

    /**
     * Translation function.
     * Usage: t('login.title') => "Giriş Yap" or "Sign In"
     * Supports simple interpolation: t('companies.registeredCount', { count: 5 }) => "5 firma kayıtlı"
     */
    const t = useCallback((key, params = {}) => {
        const keys = key.split('.');
        let value = locales[language];

        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Fallback to Turkish, then return the key itself
                let fallback = locales['tr'];
                for (const fk of keys) {
                    if (fallback && typeof fallback === 'object' && fk in fallback) {
                        fallback = fallback[fk];
                    } else {
                        return key; // Key not found in any locale
                    }
                }
                value = fallback;
                break;
            }
        }

        if (typeof value !== 'string') return key;

        // Simple interpolation: replace {key} with params[key]
        return value.replace(/\{(\w+)\}/g, (_, paramKey) => {
            return params[paramKey] !== undefined ? params[paramKey] : `{${paramKey}}`;
        });
    }, [language]);

    const value = { language, setLanguage, toggleLanguage, t };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}

export default LanguageContext;
